import { type ComponentProps, type ReactNode, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useAdDetailsContext } from '../../../../contexts/AdDetailsContext';
import { useVerificationContext } from '../../../../contexts/VerificationContext';
import { useUserContext } from '../../../../contexts/UserContext';
import { pushToDataLayer } from '../../../../services/gtmService';

import { VerifyTooltip } from '../../../../components/PageWrappers/SharedComponents/VerifyTooltip/VerifyTooltip';
import { Button } from '../../../../components/Toolkit/Button/Button';

interface RevealSendMessageProps extends ComponentProps<typeof Button> {
  size?: 'SMALL' | 'LARGE' | 'GROW';
  testId?: string;
  trackingId?: string;
  clickSource?: string;
  disabled?: boolean;
  children?: ReactNode;
}

const RevealSendMessage = (props: RevealSendMessageProps) => {
  const {
    size = 'LARGE',
    testId = 'reveal-send-message',
    trackingId,
    clickSource = '',
    disabled = false,
    children = 'Send Message',
    ...rest
  } = props;
  const { setIsSendMessageModalOpen } = useAdDetailsContext();
  const { triggerAfterVerify, setTriggerAfterVerify } =
    useVerificationContext();
  const { userProfile, isUserVerified } = useUserContext();
  const { handleLogin } = useUserContext();
  const { asPath } = useRouter();

  useEffect(() => {
    if (triggerAfterVerify && isUserVerified) {
      setTriggerAfterVerify(false);
      handleReveal();
    }
    // TODO Clean up this effect's dependencies. We're disabling this lint error for now so we can
    // clean up the lint logs. Ideally we should rewrite this code to be less error prone and trust
    // the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerAfterVerify]);

  async function handleReveal() {
    pushToDataLayer({ event: 'message_intent', source: clickSource });
    setIsSendMessageModalOpen(true);
  }
  return (
    <>
      {userProfile && !isUserVerified ? (
        <VerifyTooltip
          arrowPosition={['right', 'right', 'center']}
          tooltipPosition={'above'}
          text="You’ll need to verify your email or phone number before you can send enquiries"
        >
          <Button
            disabled={disabled}
            size={size}
            data-testid={testId}
            data-tracking={trackingId}
            {...rest}
          >
            {children}
          </Button>
        </VerifyTooltip>
      ) : (
        <Button
          disabled={disabled}
          size={size}
          onClick={userProfile ? handleReveal : () => handleLogin(asPath)}
          data-testid={testId}
          data-tracking={trackingId}
          {...rest}
        >
          {children}
        </Button>
      )}
    </>
  );
};

export { RevealSendMessage };
